import $ from "jquery";
import ko from "knockout";
import "jquery-ui/ui/widgets/datepicker";
import dateHelper from "flow/utils/dateHelper";
import metaCache from "flow/utils/metaCache";
import register from "flow/knockout/register";

function setup() {
    ko.validation.rules["dateBindingValidation"] = {
        validator: function (val: string) {
            if (!val || val.length === 0) {
                return true;
            }
            return dateHelper.isValidDateFormat(val);
        },
        message: metaCache.lookup("FlowHtml.Translations.InValidDate", "FlowHtml.Translations.InValidDate")
    };

    ko.validation.rules["validMaxDate"] = {
        validator: function (val: string, obj: any) {
            const maxDate = $(obj.target).datepicker("option", "maxDate");
            if (!val || val.length === 0 || !maxDate) {
                return true;
            }
            return dateHelper.isValidDateFormat(val) && dateHelper.isValidInterval(val, maxDate);
        },
        message: metaCache.lookup(
            "FlowHtml.Translations.FutureDateNotAllowed",
            "FlowHtml.Translations.FutureDateNotAllowed"
        )
    };

    ko.validation.rules["validMinDate"] = {
        validator: function (val: string, obj: any) {
            const minDate = $(obj.target).datepicker("option", "minDate");
            if (!val || val.length === 0 || !minDate) {
                return true;
            }
            return dateHelper.isValidDateFormat(val) && dateHelper.isValidInterval(minDate, val);
        },
        message: metaCache.lookup(
            "FlowHtml.Translations.PastDateNotAllowed",
            "FlowHtml.Translations.PastDateNotAllowed"
        )
    };

    register.registerKnockoutBinding("datev2", {
        after: ["attr"],
        init: function (element, valueAccessor, allBindingsAccessor, viewModel, bindingContext) {
            ko.bindingHandlers.value.init(element, valueAccessor, allBindingsAccessor, viewModel, bindingContext);
            const observable: KnockoutObservable<any> = valueAccessor();
            const subscriptions: KnockoutSubscription[] = [];

            const datepickerOptionsParams = (allBindingsAccessor().datepickerOptions ||
                {}) as JQueryUI.DatepickerOptions;
            const options: Partial<JQueryUI.DatepickerOptions> = Object.entries(datepickerOptionsParams).reduce(
                <T extends keyof JQueryUI.DatepickerOptions>(
                    opt: Partial<JQueryUI.DatepickerOptions>,
                    [key, value]: [key: T, value: JQueryUI.DatepickerOptions[T]]
                ) => {
                    if (ko.isSubscribable(value)) {
                        const subscription = value.subscribe(function (newValue: string) {
                            if (key === "maxDate" || key === "minDate") {
                                newValue = dateHelper.getFormattedDateString(newValue);
                            }

                            $(element).datepicker("option", key, newValue);
                        });
                        subscriptions.push(subscription);
                    }

                    opt[key] = ko.unwrap(value);

                    return opt;
                },
                {}
            );

            if (!!options.maxDate) {
                options.maxDate = dateHelper.getFormattedDateString(options.maxDate);
            } else if (
                observable.rules &&
                observable.rules() &&
                observable.rules().some((rule: any) => rule.rule === "noFutureDate")
            ) {
                options.maxDate = new Date();
            }

            options.minDate = !!options.minDate ? dateHelper.getFormattedDateString(options.minDate) : options.minDate;

            const currentRules = observable.rules ? observable.rules().map(r => r.rule) : [];

            observable.extend({
                dateBindingValidation: true,
                validMaxDate: { target: element },
                validMinDate: { target: element }
            });

            const newRules = observable
                .rules()
                .filter(r => !currentRules.includes(r.rule))
                .map(r => r.rule);

            options.onClose = function () {
                const observable = valueAccessor();
                observable($(this).val());
                $(element).focus();
            };

            if (allBindingsAccessor().positionAbove) {
                options["beforeShow"] = function (input: HTMLInputElement, inst: any) {
                    const topVal = $(input).offset().top - (inst.dpDiv.height() + $(input).height() + 10); // 5px spacing
                    setTimeout(function () {
                        inst.dpDiv.css("top", topVal + "px");
                    }, 0);
                    return null;
                };
            }

            options.showOn = "never";

            const result = $(element).parents(".input-group");
            const button = $(result).find(".btn");

            const enableBinding = allBindingsAccessor.get("enable");
            if (enableBinding && $(button).length === 1) {
                if (ko.isObservable(enableBinding) || ko.isComputed(enableBinding)) {
                    const enableSubscription = enableBinding.subscribe(function (enabled) {
                        if (enabled) {
                            $(button).prop("disabled", false);
                        } else {
                            $(button).prop("disabled", true);
                        }
                    });
                    $(button).prop("disabled", !enableBinding());
                    subscriptions.push(enableSubscription);
                }
            }
            if (enableBinding === false) {
                $(button).prop("disabled", true);
            }
            const disableBinding = allBindingsAccessor.get("disable");
            if (disableBinding && $(button).length === 1) {
                if (ko.isObservable(disableBinding) || ko.isComputed(disableBinding)) {
                    const disableSubscription = disableBinding.subscribe(function (disabled) {
                        if (disabled) {
                            $(button).prop("disabled", true);
                        } else {
                            $(button).prop("disabled", false);
                        }
                    });
                    $(button).prop("disabled", disableBinding());
                    subscriptions.push(disableSubscription);
                }
            }
            if (disableBinding === true) {
                $(button).prop("disabled", true);
            }

            $(element).datepicker(options);

            if (button.length === 0) {
                // No button, we are probably in table and should show calendar on focus.
                $(element).click(function () {
                    $(element).datepicker("show");
                });
            } else {
                // Button, show calendar on button but not on focus of input field.
                $(button).click(function () {
                    // do not open calender if the element is disabled
                    if (!$(element).attr("disabled")) $(element).datepicker("show");
                });
            }

            function onFieldChange() {
                const observable = valueAccessor();
                if (typeof observable !== "function") {
                    console.log(element);
                }
                observable($(element).val());
            }

            //handle the field changing
            element.addEventListener("change", onFieldChange);

            //handle disposal (if KO removes by the template binding)
            ko.utils.domNodeDisposal.addDisposeCallback(element, () => {
                element.removeEventListener("change", onFieldChange);
                $(element).datepicker("destroy");
                subscriptions.forEach(s => s.dispose());
                newRules.forEach(r => observable.rules.remove(cr => cr.rule === r));
            });
        },
        update: function (element, valueAccessor, allBindingsAccessor, viewModel, bindingContext) {
            ko.bindingHandlers.value.update(element, valueAccessor, allBindingsAccessor, viewModel, bindingContext);
            let value = ko.utils.unwrapObservable(valueAccessor());

            //handle date data coming via json from Microsoft
            if (String(value).indexOf("/Date(") == 0) {
                value = new Date(parseInt(value.replace(/\/Date\((.*?)\)\//gi, "$1")));
            }

            const newDate = dateHelper.formatIfComplete(value);
            const current = $(element).val();

            if (newDate !== current) {
                $(element).val(newDate);
            }
        }
    });
}

export default {
    setup: setup
};
